import { useI18n, type MessageKey } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useUpdateOrderStatusMutation } from '../api';
import { sourceLabel } from '../attribution';
import { type UpdateOrderStatusFormValues } from '../schemas';
import type { MerchantDrivenStatus, MerchantOrder, OrderStatus } from '../types';

import { CANCELLABLE_ORDER_STATUSES, CancelOrderModal } from './CancelOrderModal';
import { OrderStatusBadge } from './OrderStatusBadge';
import { SendToJibexModal } from './SendToJibexModal';
import { UpdateStatusForm } from './UpdateStatusForm';

import { useToast } from '@/components/ui/toast/ToastProvider';
import { SHOP_TIME_ZONE } from '@/features/analytics/activity';
import { formatCatalogPrice } from '@/features/products/format-price';

const ACTION_LABEL_KEY: Record<MerchantDrivenStatus, MessageKey> = {
  PROCESSING: 'merchant.orders.actionProcessing',
  PACKED: 'merchant.orders.actionPacked',
  CANCELLED: 'merchant.common.cancel',
};

/**
 * The single, deliberately narrow next-status HINT this table offers —
 * a UX affordance, never the authority. `OrderStateMachine`, server-side,
 * remains the only real decision-maker (DEV §9.4): this returns `null`
 * for every status where no merchant-driven transition is currently
 * both legal AND real for a COD order —
 *
 *   - `PROCESSING → CANCELLED` exists in the machine but is guarded
 *     DISABLED pending OQ-05 (SCORE) — offering it would show a button
 *     that always fails; not offered.
 *   - Every other status (PACKED, CANCELLED, or anything not yet
 *     reachable — SHIPPED/DELIVERED/etc., entrée 67) has no
 *     merchant-driven transition defined by DEV §9.4 at all.
 *
 * This is NOT the transition table reimplemented: it names exactly the
 * two rows that are both real and reachable today, nothing more — the
 * API still re-validates and would refuse anything this function got
 * wrong.
 */
function nextMerchantStatus(status: OrderStatus): MerchantDrivenStatus | null {
  if (status === 'PAYMENT_CONFIRMED') return 'PROCESSING';
  if (status === 'PROCESSING') return 'PACKED';
  return null;
}

/**
 * The same two statuses `nextMerchantStatus` above offers a real action
 * for — exported so the Dashboard's "commandes à traiter" alert
 * (`features/dashboard/api.ts`) counts exactly what this table itself
 * would let the merchant act on, never a separately-invented definition
 * of "needs attention".
 */
export const ACTIONABLE_ORDER_STATUSES: readonly OrderStatus[] = [
  'PAYMENT_CONFIRMED',
  'PROCESSING',
];

function reportError(
  error: unknown,
  toast: ReturnType<typeof useToast>,
  t: ReturnType<typeof useI18n>['t'],
): void {
  toast.error(t(resolveErrorMessageKey(error)));
}

/** « 30 oct., 14:32 » in the shop's own time zone (Tunis), whatever the device's. */
function formatOrderDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-TN' : locale === 'en' ? 'en-GB' : 'fr-TN', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: SHOP_TIME_ZONE,
  }).format(new Date(iso));
}

export function OrderTable({ orders }: { orders: readonly MerchantOrder[] }): ReactElement {
  const { t, locale } = useI18n();
  const toast = useToast();
  const updateStatus = useUpdateOrderStatusMutation();
  const [editing, setEditing] = useState<{ id: string; target: MerchantDrivenStatus } | null>(null);
  // « Confirmer et envoyer à Jibex »: the order a merchant reviews before it becomes « Préparée » and is sent.
  const [sending, setSending] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<MerchantOrder | null>(null);

  async function markPackedWithoutCarrier(): Promise<void> {
    if (sending === null) return;
    try {
      await updateStatus.mutateAsync({ orderId: sending, status: 'PACKED' });
      toast.success(t('merchant.orders.statusUpdated'));
      setSending(null);
    } catch (error) {
      reportError(error, toast, t);
    }
  }

  async function onSubmit(values: UpdateOrderStatusFormValues): Promise<void> {
    if (editing === null) return;
    try {
      await updateStatus.mutateAsync({
        orderId: editing.id,
        status: values.status,
        ...(values.reason !== undefined ? { reason: values.reason } : {}),
      });
      toast.success(t('merchant.orders.statusUpdated'));
      setEditing(null);
    } catch (error) {
      reportError(error, toast, t);
    }
  }

  /** The row's actions, the same in the table and in the phone list (one source, never two to keep in sync). */
  function renderActions(order: MerchantOrder, layout: 'table' | 'phone'): ReactElement {
    const next = nextMerchantStatus(order.status);
    return (
      <div
        className={
          layout === 'table'
            ? 'flex items-center justify-end gap-2'
            : 'flex flex-wrap items-center gap-2 [&>*]:min-h-10'
        }
      >
        {next === 'PACKED' ? (
          // The order is sent to Jibex from a review of the parcel, not a bare status change.
          <Button
            size="sm"
            onClick={() => {
              setSending(order.id);
            }}
          >
            {t('merchant.sendJibex.action')}
          </Button>
        ) : next !== null ? (
          <Button
            size="sm"
            onClick={() => {
              setEditing({ id: order.id, target: next });
            }}
          >
            {t(ACTION_LABEL_KEY[next])}
          </Button>
        ) : order.status === 'PACKED' ? (
          // Already « Préparée »: see the parcel, or correct and re-send a send Jibex refused.
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setSending(order.id);
            }}
          >
            {t('merchant.sendJibex.action')}
          </Button>
        ) : (
          <span className="flex items-center text-xs text-inkFaint">
            {order.status === 'CANCELLED' ||
            order.status === 'DELIVERED' ||
            order.status === 'REFUNDED'
              ? t('merchant.orders.completed')
              : t('merchant.orders.noActionNeeded')}
          </span>
        )}
        {CANCELLABLE_ORDER_STATUSES.includes(order.status) ? (
          <Button
            size="sm"
            variant="secondary"
            aria-label={t('merchant.cancelOrder.title', { number: order.orderNumber })}
            onClick={() => {
              setCancelling(order);
            }}
          >
            {t('merchant.cancelOrder.action')}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {/* Phones: each order as a two-line row — number and total, then customer, status and date — with its actions
          under it at full thumb size. Fields move to the second line; none is dropped. */}
      <ul className="flex flex-col divide-y divide-line overflow-hidden rounded-2xl border border-line bg-surface sm:hidden">
        {orders.map((order) => (
          <li key={order.id} className="flex flex-col gap-2.5 px-4 py-3.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate">
                <span className="font-mono text-sm font-semibold text-ink">
                  #{order.orderNumber}
                </span>
                <span className="ms-2 text-sm text-ink">{order.customerName}</span>
              </span>
              <span className="figure shrink-0 text-base font-semibold text-ink">
                {formatCatalogPrice(order.grandTotal)}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-inkFaint">
              <OrderStatusBadge status={order.status} />
              <span>{formatOrderDate(order.createdAt, locale)}</span>
              <span className="whitespace-nowrap">
                <span aria-hidden="true">· </span>
                {order.customerPhone}
              </span>
            </div>
            {renderActions(order, 'phone')}
          </li>
        ))}
      </ul>

      <div className="hidden sm:block">
        <Table caption={t('merchant.orders.title')}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t('merchant.orders.table.colOrder')}</TableHeaderCell>
              <TableHeaderCell>{t('merchant.orders.table.colCustomer')}</TableHeaderCell>
              <TableHeaderCell>{t('merchant.orders.table.colStatus')}</TableHeaderCell>
              <TableHeaderCell align="end">{t('merchant.orders.table.colTotal')}</TableHeaderCell>
              <TableHeaderCell>{t('merchant.orders.table.colDate')}</TableHeaderCell>
              <TableHeaderCell> </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.id}>
                <TableCell>
                  <span className="font-mono text-sm font-semibold text-ink">
                    #{order.orderNumber}
                  </span>
                  {order.source !== 'online' ? (
                    <span className="ms-2 inline-block whitespace-nowrap rounded-full bg-surface2 px-2 py-0.5 text-[11px] font-medium text-inkSoft">
                      {t(
                        order.source === 'manual_sale'
                          ? 'merchant.orders.sourceSale'
                          : 'merchant.orders.sourceManual',
                      )}
                    </span>
                  ) : null}
                  {order.attribution !== null && order.attribution.source !== 'direct' ? (
                    <span
                      className="ms-2 inline-block whitespace-nowrap rounded-full bg-surface2 px-2 py-0.5 text-[11px] font-medium text-inkSoft"
                      title={order.attribution.campaign ?? undefined}
                    >
                      {t('merchant.attribution.via', {
                        source: sourceLabel(order.attribution.source, t),
                      })}
                    </span>
                  ) : null}
                  <p className="text-xs text-inkFaint">
                    {t('merchant.orders.itemCount', {
                      count: order.items.length,
                      plural: order.items.length > 1 ? 's' : '',
                    })}
                  </p>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-ink">{order.customerName}</span>
                  <p className="text-xs text-inkFaint">{order.customerPhone}</p>
                </TableCell>
                <TableCell>
                  <OrderStatusBadge status={order.status} />
                </TableCell>
                <TableCell align="end">
                  <span className="num text-sm font-semibold text-ink">
                    {formatCatalogPrice(order.grandTotal)}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="whitespace-nowrap text-xs text-inkFaint">
                    {formatOrderDate(order.createdAt, locale)}
                  </span>
                </TableCell>
                <TableCell align="end">{renderActions(order, 'table')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Modal
        open={editing !== null}
        title={t('merchant.orders.updateModalTitle')}
        onClose={() => {
          setEditing(null);
        }}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(null);
              }}
            >
              {t('merchant.common.cancel')}
            </Button>
            <Button type="submit" form="update-order-status-form" loading={updateStatus.isPending}>
              {t('merchant.common.confirm')}
            </Button>
          </>
        }
      >
        {editing !== null ? (
          <UpdateStatusForm
            targetStatus={editing.target}
            onSubmit={(values) => {
              void onSubmit(values);
            }}
            isSubmitting={updateStatus.isPending}
          />
        ) : null}
      </Modal>

      <CancelOrderModal
        order={cancelling}
        onClose={() => {
          setCancelling(null);
        }}
      />

      <SendToJibexModal
        orderId={sending}
        onClose={() => {
          setSending(null);
        }}
        onMarkPacked={() => {
          void markPackedWithoutCarrier();
        }}
        markingPacked={updateStatus.isPending}
      />
    </>
  );
}
